import type { Card, DeckId } from '@/core/types'

/** ชิปกรองบนหน้าฝึก — "D1 ธรรมชาติ", "แถว か", "hiragana" */
export interface Group {
  tag: string
  label: string
  /** จัดกลุ่มชิปเป็นแถว — "ชุด" / "แถว" / "ระบบ" */
  section: string
}

export interface Deck {
  id: DeckId
  /** ชื่อญี่ปุ่นบนหัวข้อ */
  titleJa: string
  /** คำอธิบายไทย */
  titleTh: string
  /**
   * deck ที่ข้อมูลใหญ่เกินจะ bundle (kanji 450 KB) โหลดตอนเลือกครั้งแรก
   * เรียกซ้ำได้ ไม่โหลดใหม่
   */
  ensureLoaded?: () => Promise<void>
  /** โหมดตอบเริ่มต้น — ผู้ใช้สลับได้ถ้า deck รองรับหลายโหมด */
  modes: ModeId[]
  /** ใช้ SRS (FSRS) หรือ drill ถ่วงน้ำหนัก */
  scheduling: 'srs' | 'drill'
  cards: Card[]
  groups: Group[]
  /** section ของ Group ที่ใช้เป็นคอลัมน์ (kana) หรือกลุ่มย่อย (bushu) ในตารางเลือกการ์ด */
  gridSection?: string
  /**
   * แยกตารางเป็นบล็อกใหญ่ตาม section นี้อีกชั้น
   * kana ต้องมี ไม่งั้น あいうえお กับ アイウエオ อยู่แถวเดียวกันจนแยกด้วยตาไม่ออก
   */
  gridSplit?: string
  /**
   * แบ่งตารางย่อยที่ความสูงต่างกัน — ตารางพื้นฐานสูง 5 ช่อง (สระ) ตารางควบสูง 3 (ゃゅょ)
   * ไม่ใส่ = ตารางเดียว เรียงแบบ wrap ไม่ต้องมี extra.slot
   */
  gridSubsets?: ReadonlyArray<{ tags: string[]; label: string; slots: number }>
}

export type ModeId =
  /*
   * อันแรกไม่ใช่ "วิธีถาม" แต่เป็น "วิธีเลือกใบ" — ระบบเลือกวิธีถามให้เอง
   * แยกไว้คนละกลุ่มกับที่เหลือ เพราะเป็นคนละคำถาม
   */
  | 'auto' // ระบบจัดให้ — ทวนที่ถึงกำหนดก่อน แล้วแทรกใบใหม่
  | 'glyph-to-romaji' // เห็นตัวอักษร → พิมพ์เสียง
  | 'romaji-to-glyph' // เห็นเสียง → เลือกตัวอักษร
  | 'audio-to-glyph' // ได้ยิน → เลือกตัวอักษร
  | 'glyph-to-meaning' // เห็นตัวอักษร → เลือกความหมาย
  | 'meaning-to-glyph' // เห็นความหมาย → เลือกตัวอักษร
  | 'recall' // นึกในใจ → เฉลย → ให้เกรดตัวเอง
  | 'breakdown' // เห็นคันจิ → เลือกชิ้นส่วนที่ประกอบให้ครบ
  | 'lesson' // เรียนใบใหม่ — ดู เข้าใจ ผูกเรื่อง ไม่มีถูกผิด

export const MODE_LABEL: Record<ModeId, string> = {
  auto: 'ฝึกอัตโนมัติ',
  'glyph-to-romaji': 'ตัวอักษร → เสียง',
  'romaji-to-glyph': 'เสียง → ตัวอักษร',
  'audio-to-glyph': 'ฟัง → ตัวอักษร',
  'glyph-to-meaning': 'ตัวอักษร → ความหมาย',
  'meaning-to-glyph': 'ความหมาย → ตัวอักษร',
  recall: 'นึกเอง (ไม่มีตัวเลือก)',
  breakdown: 'แตกชิ้นส่วน',
  lesson: 'เรียนใหม่',
}

/**
 * โหมดเรียน ไม่ใช่โหมดทดสอบ — ไม่มีถูกผิด ไม่นับสถิติ
 * การ์ดที่ไม่เคยเห็นควรผ่านตรงนี้ก่อน ไม่ใช่โผล่มาให้เดาแล้วผิดทุกใบ
 */
export function isLessonMode(m: ModeId): boolean {
  return m === 'lesson'
}

/** คีย์ลัดที่ใช้ได้จริงในโหมดนั้น — บอกผิดโหมดแล้วกดตามคือเสียเวลาเปล่า */
export const MODE_HINT: Record<ModeId, string> = {
  auto: 'ระบบจัดใบให้เอง — ตอบไปเรื่อย ๆ · <kbd>Space</kbd> ข้อถัดไป · <kbd>W</kbd> ลำดับขีด',
  lesson: '<kbd>Space</kbd> เข้าใจแล้ว · <kbd>F</kbd> ฟังเสียง · <kbd>W</kbd> ลำดับขีด',
  'glyph-to-romaji':
    'พิมพ์ตอบได้เลย · <kbd>Space</kbd> ข้อถัดไป · <kbd>F</kbd> ฟังเสียง · <kbd>/</kbd> ดูเฉลย',
  'audio-to-glyph':
    'ฟังแล้วเลือก · <kbd>F</kbd> ฟังอีกครั้ง · <kbd>1</kbd>–<kbd>4</kbd> เลือก · <kbd>/</kbd> ดูเฉลย',
  'romaji-to-glyph': '<kbd>1</kbd>–<kbd>4</kbd> เลือก · <kbd>Space</kbd> ข้อถัดไป · <kbd>/</kbd> ดูเฉลย',
  'glyph-to-meaning':
    '<kbd>1</kbd>–<kbd>4</kbd> เลือก · <kbd>Space</kbd> ข้อถัดไป · <kbd>W</kbd> ลำดับขีด · <kbd>/</kbd> ดูเฉลย',
  'meaning-to-glyph': '<kbd>1</kbd>–<kbd>4</kbd> เลือก · <kbd>Space</kbd> ข้อถัดไป · <kbd>/</kbd> ดูเฉลย',
  recall:
    '<kbd>Space</kbd> เปิดเฉลย แล้ว <kbd>1</kbd>–<kbd>4</kbd> ให้เกรดตัวเอง · <kbd>W</kbd> ลำดับขีด',
  breakdown: 'เลือกชิ้นส่วนให้ครบทุกชิ้น · <kbd>Space</kbd> ข้อถัดไป',
}

/** โหมดที่ระบบเลือกใบให้เอง — แยกกลุ่มจากโหมดที่เจาะจงวิธีถาม */
export function isPickerMode(m: ModeId): boolean {
  return m === 'auto'
}

/** โหมดนี้ตอบด้วยการพิมพ์หรือเลือก */
export function isTypingMode(m: ModeId): boolean {
  return m === 'glyph-to-romaji'
}


export type { Card, DeckId }
